import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { filter, isEmpty, size } from 'lodash'
import { Box, Button, Typography } from '@material-ui/core'

import {
  AlertDialog,
  Container,
  ContentHeader,
  LoadingFeedback,
  Page,
  PermittedByAssignmentRole,
} from 'components'

import {
  ActivityAlert,
  ActivityManageForm,
  AuditAnswers,
  Contestation,
  ReviewOpinion,
  RiskAssumptionMenu,
} from './components'
import helpers from 'helpers'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'
import useAssignmentPermission from 'hooks/useAssignmentPermission'
import useAuth from 'hooks/useAuth'

import theme from 'theme'
import * as service from 'service'
import constants from 'constants/index'
import DaiProvider from 'providers/DaiProvider'
import useStyles from './styles'

const ActivityManage = ({ match }) => {
  const { governanceProjectId, activityId } = match.params
  const { INITIAL_STATUS_ID, HEADWAY_STATUS_ID } = constants.planAction
  const { INACTIVE_STATUS_ID, MANAGER_ROLE, PROJECT_RESPONSIBLE_ROLE } =
    constants.governanceProject
  const {
    AUDIT_AGREES_STATUS_ID,
    COMPLETED_STATUS_ID,
    IN_PROGRESS_STATUS_ID,
    PENDING_STATUS_ID,
    WITHOUT_CODE_LABEL,
  } = constants.governanceProjectActivity

  const [loading, setLoading] = useState(false)
  const [planActionsOpened, setPlanActionsOpened] = useState([])
  const [defaultValues, setDefaultValues] = useState({})
  const [modifiedForm, setModifiedForm] = useState(false)
  const [openAlertDialog, setOpenAlertDialog] = useState(false)
  const [currentTab, setCurrentTab] = useState('submit_answers')
  const [contestationText, setContestationText] = useState('')
  const [contesting, setContesting] = useState(false)

  const alertDialogRef = useRef()
  const contestingInitialized = useRef(false)

  const classes = useStyles()
  const snackbar = useSnackbar()
  const { user } = useAuth()
  const {
    response: usersResponse,
    isLoading: usersIsLoading,
    refresh: usersRefresh,
  } = useFetch(
    service.dponet.users.get,
    {
      perPage: 10000,
      minimal: true,
      active: true,
      userCompanyStatus: constants.userCompanies.ACTIVE,
    },
    [],
  )
  const {
    response: activityResponse,
    isLoading: activityIsLoading,
    refresh: activityRefresh,
  } = useFetch(
    service.dponet.governanceProjectActivity.get,
    {
      governanceProjectId,
      id: activityId,
    },
    [],
  )

  // derivado consumido pelo hook abaixo
  const activity = activityResponse?.data?.governanceProjectActivity
  const { assignmentRole } = useAssignmentPermission({
    governanceProject: activity?.governanceProject,
  })

  const users = usersResponse?.data?.users
  const isLoadingResponse = usersIsLoading || activityIsLoading
  const question = activity?.governanceProjectQuestion
  const activityCode = question?.code || WITHOUT_CODE_LABEL
  const activityName = `[${activityCode}] ${
    question?.theme || 'Atividade do Projeto de Governança'
  }`
  const isCoveredByReview = !!activity?.coveredByReview

  const isCompleted = activity?.status === COMPLETED_STATUS_ID

  const isInactiveProject =
    activity?.governanceProject?.status === INACTIVE_STATUS_ID
  // true também quando o projeto pai está arquivado, não só concluído
  const isCompletedProject = helpers.governanceProject.isProjectLocked(
    activity?.governanceProject?.status,
  )

  const disabledAll = isInactiveProject || isCompletedProject

  const findTemplateStage = (stageKind) =>
    activity?.governanceProjectTemplateStages?.find(
      (stage) => stage.kind === stageKind,
    )

  const auditStage = findTemplateStage('audit_answers')
  const contestationStage = findTemplateStage('contestation')
  const auditorAnswerEquals =
    activity?.contestationStatus === AUDIT_AGREES_STATUS_ID

  const isInternalUser =
    user?.email?.endsWith('@dponet.com.br') ||
    user?.email?.endsWith('@leavening.com')
  const disputeAnalysisEnabled = helpers.dai.disputeAnalysisEnabled(activity)
  const hasDaiDisputeResponse = !!activity?.daiDisputeResponse?.runId
  // Libera acesso à DAI de contestação para o time interno antes de entrar
  // na etapa de contestação e quando a resposta do auditor já está disponível.
  const contestationEarlyAccess =
    isInternalUser &&
    auditStage?.progressStatus === COMPLETED_STATUS_ID &&
    contestationStage?.progressStatus === PENDING_STATUS_ID
  // Libera a etapa de contestação para projetos com a análise da DAI habilitada
  // nas settings e que já possuem uma resposta de contestação da DAI gerada.
  const disputeAnalysisReleased =
    disputeAnalysisEnabled &&
    hasDaiDisputeResponse &&
    auditStage?.progressStatus === COMPLETED_STATUS_ID

  const resultDisabled = () => {
    if (!isCompleted || !isCompletedProject) return true
    if (
      activity?.contestation &&
      activity?.stageContestation?.status !==
        constants.governanceProjectStages.APPROVED_STAGE_STATUS
    )
      return true
    return activity?.governanceProjectTemplateStages?.some(
      (stage) =>
        stage.progressStatus === IN_PROGRESS_STATUS_ID ||
        stage.progressStatus === PENDING_STATUS_ID,
    )
  }

  const tabs = [
    { value: 'submit_answers', label: 'Submissão', visible: true },
    {
      value: 'audit_answers',
      label: 'Parecer',
      visible: !!auditStage?.id,
      disabled: auditStage?.progressStatus !== COMPLETED_STATUS_ID,
    },
    {
      value: 'contestation',
      label: 'Contestação',
      visible: !!contestationStage?.id,
      disabled:
        auditorAnswerEquals ||
        (contestationStage?.progressStatus === PENDING_STATUS_ID &&
          !contestationEarlyAccess &&
          !disputeAnalysisReleased),
    },
    {
      value: 'result',
      label: 'Resultado final',
      visible: !!auditStage?.id || !!contestationStage?.id,
      disabled: resultDisabled(),
    },
  ]

  const visibleTabs = filter(tabs, 'visible')

  const handleRefresh = () => {
    setPlanActionsOpened([])
    setModifiedForm(false)
    usersRefresh()
    activityRefresh()
  }

  const handleOpenAlertDialog = () => {
    const hasPlanActions = activity?.evidencePlanActions?.some(
      (evidence) =>
        Array.isArray(evidence?.planActions) && size(evidence?.planActions) > 0,
    )

    if (!alertDialogRef.current && hasPlanActions) {
      setOpenAlertDialog(true)
      alertDialogRef.current = true
    }
  }
  const handleCloseAlertDialog = () => setOpenAlertDialog(false)

  const handleReOpenActivity = async () => {
    try {
      setLoading(true)

      await service.dponet.governanceProjectActivity.changeStatus({
        governanceProjectId,
        id: activity?.id,
        status: IN_PROGRESS_STATUS_ID,
      })

      snackbar.open({
        message: 'Atividade reaberta com sucesso!',
        variant: 'success',
      })
      handleRefresh()
    } catch (error) {
      console.error(error)
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!contestingInitialized.current && activity) {
      setContesting(
        activity?.contestation ||
          !isEmpty(activity?.stageContestation?.description),
      )
      contestingInitialized.current = true
    }
  }, [activity])

  useEffect(() => {
    setPlanActionsOpened((prevState) => {
      const newPlanActionIds =
        activity?.evidencePlanActions?.flatMap((action) =>
          action?.planActions
            ?.filter(
              (planAction) =>
                (planAction?.status === INITIAL_STATUS_ID ||
                  planAction?.status === HEADWAY_STATUS_ID) &&
                !prevState.includes(planAction?.id),
            )
            .map((planAction) => planAction.id)
            .filter(Boolean),
        ) || []

      return [...prevState, ...newPlanActionIds]
    })

    const values = {
      responsible: activity?.assignment || {},
      deadlineDate: activity?.deadlineDate || null,
      observations: activity?.governanceProjectQuestion?.observations || '',
      exceptionReason: activity?.exceptionReason || '',
      applicabilityReason: activity?.applicabilityReason || '',
      alternativeAnswerId: activity?.notApplicable
        ? 'not_applicable'
        : activity?.governanceProjectQuestion?.alternativeAnswer?.id?.toString() ||
          '',
      evidencePlanActions:
        activity?.evidencePlanActions?.map((action) => ({
          ...action,
          originId: action?.id,
          // Decide se nome e descrição são obrigatórios no schema e se eles vão no payload do submit.
          // Normalizado para booleano porque o schema compara com `is: true`.
          createdManually: !!action?.createdManually,
          evidence: {
            ...action.evidence,
            justify: action?.evidence?.justify || '',
            attachments: null,
          },
          planActions:
            action?.planActions?.map((plan) => ({
              id: plan?.id || '',
              originId: plan?.id || '',
              how: plan?.how || '',
              what: plan?.what || '',
              where: plan?.where || '',
              howPrice: plan?.howPrice || '',
              because: plan?.because || '',
              deadlineDate: plan?.deadlineDate || '',
              status: plan?.status || '',
              responsible: plan?.responsible || {},
            })) || [],
        })) || [],
    }

    setDefaultValues(values)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity])

  return (
    <Page title={activityName}>
      <Container maxWidth={false}>
        <ContentHeader title={activityName} goBack>
          {isCompleted &&
            !isCoveredByReview &&
            !modifiedForm &&
            !disabledAll && (
              <PermittedByAssignmentRole
                permittedRoles={[MANAGER_ROLE, PROJECT_RESPONSIBLE_ROLE]}
                userRole={assignmentRole}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleReOpenActivity}
                >
                  Reabrir Atividade
                </Button>
              </PermittedByAssignmentRole>
            )}
          <RiskAssumptionMenu
            activity={activity}
            governanceProjectId={governanceProjectId}
            activityCode={activityCode}
            assignmentRole={assignmentRole}
            refresh={handleRefresh}
          />
        </ContentHeader>
        <LoadingFeedback open={loading || isLoadingResponse} />
        <Box display="flex" flexDirection="column" gridGap={theme.spacing(3)}>
          <ActivityAlert
            activity={activity}
            modifiedForm={modifiedForm}
            action={
              helpers.governanceProject.hasSentReviewOpinion(activity) && (
                <ReviewOpinion latestReview={activity.latestReview} />
              )
            }
          />

          {visibleTabs.length > 1 && (
            <Box display="flex" className={classes.tabsContainer}>
              {visibleTabs.map((tab) => (
                <Button
                  key={tab.value}
                  variant="outlined"
                  color="primary"
                  onClick={() => setCurrentTab(tab.value)}
                  className={clsx(classes.tabButton, {
                    [classes.activeTab]: currentTab === tab.value,
                  })}
                  fullWidth
                  disabled={tab.disabled}
                >
                  <Box display="flex" justifyContent="center" width="100%">
                    <Typography variant="button">{tab.label}</Typography>
                  </Box>
                </Button>
              ))}
            </Box>
          )}
          {currentTab === 'submit_answers' && (
            <DaiProvider>
              {!isLoadingResponse && (
                <ActivityManageForm
                  users={users}
                  activity={activity}
                  governanceProjectId={governanceProjectId}
                  setStates={{
                    setLoading,
                    setPlanActionsOpened,
                    setDefaultValues,
                    setModifiedForm,
                    handleOpenAlertDialog,
                  }}
                  states={{
                    loading,
                    planActionsOpened,
                    defaultValues,
                  }}
                  refresh={handleRefresh}
                  activityName={activityName}
                  activityCode={activityCode}
                />
              )}
            </DaiProvider>
          )}
          {currentTab === 'audit_answers' && (
            <AuditAnswers
              activity={activity}
              auditorAnswerEquals={auditorAnswerEquals}
              setCurrentTab={setCurrentTab}
              activityName={activityName}
            />
          )}
          <Box display={currentTab === 'contestation' ? 'block' : 'none'}>
            <Contestation
              activity={activity}
              governanceProjectId={governanceProjectId}
              contesting={contesting}
              setContesting={setContesting}
              contestationText={
                contestationText || activity?.stageContestation?.description
              }
              setContestationText={setContestationText}
              refresh={activityRefresh}
              activityName={activityName}
              earlyAccess={contestationEarlyAccess}
            />
          </Box>
          {currentTab === 'result' && (
            <AuditAnswers
              activity={activity}
              isResult
              activityName={activityName}
            />
          )}
        </Box>
        <AlertDialog
          open={openAlertDialog}
          onClose={handleCloseAlertDialog}
          variant="standard"
          severity="info"
          title="Alteração do prazo da atividade"
          content="Alterar o prazo da atividade pode impactar os prazos dos planos de ação finalizados. Se houver planos de ação com um prazo menor, eles serão ajustados para coincidir com o novo prazo da atividade."
        />
      </Container>
    </Page>
  )
}

export default ActivityManage
